import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { AdminSidebarLayout } from './SidebarLayout';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Loader2, Save, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

export function AdminPortfolioPage() {
  const { api } = useAuth();
  const [portfolio, setPortfolio] = useState(null);
  const [unitValue, setUnitValue] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [recalculating, setRecalculating] = useState(false);

  useEffect(() => {
    fetchPortfolio();
  }, []);

  const fetchPortfolio = async () => {
    try {
      const { data } = await api.get('/api/admin/portfolio');
      setPortfolio(data);
      setUnitValue(data.current_unit_value?.toString() || '0.0000100');
    } catch (error) {
      console.error('Failed to fetch portfolio:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUnitValue = async () => {
    const val = parseFloat(unitValue);
    if (!val || val <= 0) { toast.error('Unit value must be greater than 0'); return; }
    setSaving(true);
    try {
      const { data } = await api.put('/api/admin/portfolio', { current_unit_value: val });
      toast.success(data.message || 'Unit value updated');
      fetchPortfolio();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleRecalculate = async () => {
    setRecalculating(true);
    try {
      const { data } = await api.post('/api/admin/portfolio/recalculate');
      toast.success(data.message || 'Recalculation complete');
      fetchPortfolio();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Recalculation failed');
    } finally {
      setRecalculating(false);
    }
  };

  const formatNum = (n, dec = 2) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: dec, maximumFractionDigits: dec });
  const formatShares = (n) => Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 0 });
  const parValue = 0.0000100;
  const authorizedShares = 2000000000;

  if (loading) {
    return (
      <AdminSidebarLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-[#00E5FF]" />
        </div>
      </AdminSidebarLayout>
    );
  }

  return (
    <AdminSidebarLayout>
      <div className="p-6 lg:p-8 max-w-5xl" data-testid="admin-portfolio-page">
        <div className="mb-8">
          <h1 className="font-outfit text-2xl sm:text-3xl font-semibold text-white">Portfolio Management</h1>
          <p className="text-[#A0B4CC] mt-1">Stock certificate & membership unit value management</p>
        </div>

        {/* Stock Certificate Info */}
        <Card className="bg-gradient-to-r from-[#041A3B] to-[#0A254D] border-[#00E5FF]/20 mb-6">
          <CardContent className="p-6">
            <h2 className="font-outfit text-lg font-semibold text-white mb-4">Stock Certificate</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-[#5C7599] text-[10px] uppercase tracking-wider">Authorized Shares</p>
                <p className="text-white font-mono text-lg font-bold">{formatShares(authorizedShares)}</p>
              </div>
              <div>
                <p className="text-[#5C7599] text-[10px] uppercase tracking-wider">Par Value (USD)</p>
                <p className="text-white font-mono text-lg font-bold">${parValue.toFixed(7)}</p>
              </div>
              <div>
                <p className="text-[#5C7599] text-[10px] uppercase tracking-wider">Shares Issued</p>
                <p className="text-[#00E676] font-mono text-lg font-bold">{formatShares(portfolio?.total_shares_issued)}</p>
              </div>
              <div>
                <p className="text-[#5C7599] text-[10px] uppercase tracking-wider">Unissued Shares</p>
                <p className="text-[#A0B4CC] font-mono text-lg font-bold">{formatShares(portfolio?.unissued_shares || (authorizedShares - (portfolio?.total_shares_issued || 0)))}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Membership Pool Stats */}
        <div className="grid md:grid-cols-3 gap-4 mb-6">
          <Card className="bg-[#041A3B] border-white/10">
            <CardContent className="p-5">
              <p className="text-[#5C7599] text-xs uppercase tracking-wider mb-1">Total Membership Pool (PHP)</p>
              <p className="text-white font-outfit text-2xl font-bold">PHP {formatNum(portfolio?.total_subscription_php)}</p>
              <p className="text-[#5C7599] text-xs mt-1">{portfolio?.total_members_with_subscription || 0} members</p>
            </CardContent>
          </Card>
          <Card className="bg-[#041A3B] border-white/10">
            <CardContent className="p-5">
              <p className="text-[#5C7599] text-xs uppercase tracking-wider mb-1">Total Membership Pool (USD)</p>
              <p className="text-white font-outfit text-2xl font-bold">${formatNum(portfolio?.total_subscription_usd, 4)}</p>
              <p className="text-[#5C7599] text-xs mt-1">Rate: 1 PHP = ${(portfolio?.php_usd_rate || 0).toFixed(6)} USD</p>
            </CardContent>
          </Card>
          <Card className="bg-[#041A3B] border-white/10">
            <CardContent className="p-5">
              <p className="text-[#5C7599] text-xs uppercase tracking-wider mb-1">Last Recalculated</p>
              <p className="text-white font-outfit text-sm font-medium">
                {portfolio?.last_recalculated ? new Date(portfolio.last_recalculated).toLocaleString() : 'Never'}
              </p>
              <Button onClick={handleRecalculate} disabled={recalculating} size="sm" variant="ghost" className="text-[#00E5FF] mt-2 h-8 text-xs" data-testid="recalculate-btn">
                {recalculating ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                Recalculate Now
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Unit Value Management */}
        <Card className="bg-[#041A3B] border-white/10 mb-6">
          <CardContent className="p-6">
            <h2 className="font-outfit text-lg font-semibold text-white mb-2">Current Unit Value</h2>
            <p className="text-[#5C7599] text-sm mb-4">
              Update the unit value based on real business valuation, earnings, or approved internal valuation.
              This directly affects the Membership Unit Value shown in member wallets.
            </p>
            <div className="flex items-end gap-4">
              <div className="flex-1 max-w-xs space-y-2">
                <Label className="text-[#A0B4CC] text-xs">Unit Value (USD per share)</Label>
                <Input
                  type="number"
                  step="0.0000001"
                  value={unitValue}
                  onChange={e => setUnitValue(e.target.value)}
                  className="bg-[#000B18] border-white/20 text-white font-mono"
                  data-testid="unit-value-input"
                />
              </div>
              <Button onClick={handleUpdateUnitValue} disabled={saving} className="bg-[#00E5FF] text-[#000B18] hover:bg-[#00E5FF]/90 h-10" data-testid="update-unit-value-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" /> Update & Recalculate</>}
              </Button>
            </div>
            <p className="text-[#5C7599] text-xs mt-3">
              Par value: ${parValue.toFixed(7)} | Current: ${parseFloat(unitValue || 0).toFixed(7)}
              {parseFloat(unitValue || 0) > parValue && (
                <span className="text-[#00E676] ml-2">
                  (+{(((parseFloat(unitValue) - parValue) / parValue) * 100).toFixed(2)}% above par)
                </span>
              )}
            </p>
          </CardContent>
        </Card>

        {/* Disclaimer */}
        <div className="p-4 rounded-lg bg-[#041A3B]/50 border border-white/5">
          <p className="text-[#5C7599] text-xs">
            <strong>Note:</strong> The membership unit value is recalculated daily at midnight using the live PHP/USD exchange rate.
            Member wallets show their allocated shares and corresponding membership unit value in PHP.
            Subscription values are non-withdrawable and non-transferable. Only referral commissions and incentives are withdrawable.
          </p>
        </div>
      </div>
    </AdminSidebarLayout>
  );
}
